import React, { useEffect } from "react";
import { Maximize2, X, Printer } from "lucide-react";

// Letter landscape printable area at 96 css px/in with 0.4in margins.
const PAGE_W = 10.2 * 96;
const PAGE_H = 7.4 * 96;

// Prints only the expanded chart (by testid) on one landscape page.
export function PrintChartButton({ targetTestId, caption }) {
  const print = () => {
    const el = document.querySelector(`[data-testid="${targetTestId}"]`);
    if (!el) return;
    const cap = document.createElement("div");
    cap.className = "print-caption";
    cap.textContent = caption;
    el.appendChild(cap);
    const r = el.getBoundingClientRect();
    const prev = { w: el.style.width, h: el.style.height, mw: el.style.maxWidth };
    // Freeze screen size so the paper re-layout doesn't reflow the fixed-size SVG.
    el.style.width = `${r.width}px`;
    el.style.height = `${r.height}px`;
    el.style.maxWidth = "none";
    el.style.setProperty("--print-zoom", String(Math.min(1, PAGE_W / r.width, (PAGE_H - 24) / r.height)));
    el.classList.add("print-target");
    document.body.classList.add("print-illus");
    const done = () => {
      el.classList.remove("print-target");
      el.style.removeProperty("--print-zoom");
      el.style.width = prev.w;
      el.style.height = prev.h;
      el.style.maxWidth = prev.mw;
      document.body.classList.remove("print-illus");
      cap.remove();
      window.removeEventListener("afterprint", done);
    };
    window.addEventListener("afterprint", done);
    window.print();
  };
  return (
    <button
      type="button"
      onClick={print}
      className="fixed z-[60] left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-white text-navy border border-navy hover:bg-navy hover:text-white px-3 py-1 text-[12px] font-semibold rounded-[3px] shadow-lg transition-colors"
      style={{ top: 10 }}
      data-testid="illus-print-btn"
    >
      <Printer size={13} /> Print / Save PDF
    </button>
  );
}

// Illustrations panel wrapper — adds a small expand icon to the header row.
export function ExpandablePanel({ id, onExpand, children, noExpand = false }) {
  if (noExpand) {
    return <div className="relative flex flex-col" style={{ height: "100%", minHeight: 0 }}>{children}</div>;
  }
  return (
    <div className="illus-expand relative flex flex-col" style={{ height: "100%", minHeight: 0 }}>
      {children}
      <button
        type="button"
        onClick={() => onExpand(id)}
        className="illus-expand-btn absolute text-navy/70 hover:text-navy hover:bg-navy-wash rounded-[3px] transition-colors"
        title="Expand"
        aria-label="Expand"
        data-testid={`illus-expand-${id}`}
      >
        <Maximize2 size={12} />
      </button>
    </div>
  );
}

// Large viewer for panels that have no dedicated full-size version.
export function ExpandModal({ id, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(20, 30, 45, 0.55)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
      data-testid={`illus-expanded-${id}`}
    >
      <div
        className="relative bg-white shadow-2xl rounded-[8px]"
        style={{ width: "min(1200px, 92vw)", height: "min(720px, 86vh)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid={`illus-expanded-box-${id}`}
      >
        {children}
        <button
          type="button"
          onClick={onClose}
          className="absolute text-slate hover:text-coral transition-colors"
          style={{ top: -30, right: 0, color: "#fff" }}
          title="Close (Esc)"
          data-testid="illus-expanded-close"
        >
          <X size={22} />
        </button>
      </div>
    </div>
  );
}
